import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import moment from "moment";
import ArrowbackIcon from "../../assets/icons/arrow_back.svg";
import DraftIcon from "../../assets/icons/contract_edit.svg";
import SubmitIcon from "../../assets/icons/inventory.svg";
import FilePdfIcon from "../../assets/icons/file-pdf.svg";
import FileImgIcon from "../../assets/icons/file-img.svg";
import FileDocIcon from "../../assets/icons/file-doc.svg";
import FileDefaultIcon from "../../assets/icons/file-default.svg";
import CloseIcon from "../../assets/icons/close-2hite.svg";
import DeleteIcon from "../../assets/icons/delete.svg";
import ValidIcon from "../../assets/icons/emergency_home_white.svg";
import InitialsCircle from "../../components/InitialsCircle";
import { REVIEW_REQUEST_SECTIONS } from "../../constants/reviewRequest";
import { commonFields } from "../../constants/people";
import { ONBOARDING_STATUS } from "../../constants/onboarding";
import {
  getReviewRequestById,
  REVIEW_SECTION_FIELDS,
} from "../../data/reviewRequestDetails";
import FlagModal from "../../components/onboardingModal/FlagModal";
import ConfirmCreateIdModal from "../../components/onboardingModal/ConfirmCreateIdModal";
import ConfirmSendBackModal from "../../components/onboardingModal/ConfirmSendBackModal";
import SuccessCreateIdModal from "../../components/onboardingModal/SuccessCreateIdModal";
import UpdatedFieldsFilter, {
  UPDATED_FIELDS_FILTER,
} from "./Sections/UpdatedFieldsFilter";
import { getEducationQualificationLabel } from "../../utils/educationHelpers";
import {
  buildCreateIdPayload,
  buildFlagsFromApprovalFeedback,
  buildSaveProgressPayload,
  buildSendBackPayload,
  buildUpdatedFieldsFromApprovalFeedback,
  extractEmployeeIdFromSaveProgress,
  extractPresignedUrl,
  extractRequestVersion,
  extractVersionFromSaveProgress,
  countHrEditedFields,
  hrEditedFieldsCountLabel,
  isResubmittedSummaryStatus,
  mergeCopiedJobDetailsIntoForm,
  normalizeApprovalDetails,
  normalizeCopiedEmployeeDetails,
  normalizeJobDetailsDropdown,
  resolveDocumentFileKey,
  resolveDomainId,
  hasInvalidDateOfJoiningValue,
  validateJobDetailsForCreateId,
} from "../../utils/onboardingHelpers";
import {
  copyEmployeeDetails,
  generatePresignUrl,
  getCandidateApprovalDetails,
  getJobDetailsDropdown,
  markAsReviewedAndCreateId,
  saveOnboardingProgress,
  sendBackForCorrection,
} from "../../store/OnboardingSlice/thunk";
import { onboardingActions } from "../../store/OnboardingSlice";
import { modalActions } from "../../store/ModalSlice";
import {
  getFlagKey,
  isFlagBlockedField,
  isUpdatedReviewField,
} from "./Sections/ReviewField";
import PersonalSection from "./Sections/PersonalSection";
import AddressSection from "./Sections/AddressSection";
import EducationSection from "./Sections/EducationSection";
import EmploymentSection, {
  BLANK_EMPLOYMENT_EXPERIENCE_FLAGS,
  hasMissingBlankExperienceRemark,
} from "./Sections/EmploymentSection";
import BankSection from "./Sections/BankSection";
import InsuranceSection from "./Sections/InsuranceSection";
import DeclarationSection from "./Sections/DeclarationSection";
import JobDetailsSection from "./Sections/JobDetailsSection";
import { HrDirectEditContext } from "./HrDirectEditContext";
import useHrDirectEdit from "./useHrDirectEdit";
import useHrEditHistoryView from "./useHrEditHistoryView";
import HrEditHistoryPanel from "./Sections/HrEditHistory";
import { isHrApprovalActionLocked } from "../../utils/hrDirectEdit";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "../People/People.css";
import "../../components/AddEmployeeForm/AddEmployeeForm.css";
import "./ReviewRequest.css";

const PDF_WORKER_URL =
  "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

const findReviewField = (sectionKey, fieldKey) => {
  const sectionFields = REVIEW_SECTION_FIELDS[sectionKey] || [];
  const sharedFields =
    sectionKey === "employment"
      ? REVIEW_SECTION_FIELDS.employmentShared || []
      : sectionKey === "education"
        ? REVIEW_SECTION_FIELDS.educationShared || []
        : [];
  return [...sectionFields, ...sharedFields].find(
    (item) => item.key === fieldKey,
  );
};

/** Seed field flags from backend approval_feedback saved via Save as Draft */
const buildInitialFieldFlags = (approvalFeedback = {}) =>
  buildFlagsFromApprovalFeedback(approvalFeedback, {
    findField: findReviewField,
    isFlagBlocked: isFlagBlockedField,
    getFlagKey,
  });

const formatFlagTime = (isoString) => {
  if (!isoString) return "";
  return moment(isoString).format("DD MMM YYYY, hh:mm A");
};

const getFileIcon = (type) => {
  if (type === "pdf") return FilePdfIcon;
  if (type === "img") return FileImgIcon;
  if (type === "doc") return FileDocIcon;
  return FileDefaultIcon;
};

const resolveDocUrl = (url) => {
  if (!url) return "";
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const path = String(url).replace(/^\//, "");
  return `${base}/${path}`;
};

const formatCreatedEmployeeId = (jobEmployeeId) => {
  const raw = String(jobEmployeeId || "").trim();
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  return `Employee id: ${digits}`;
};

const ReviewRequest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const { requestId } = useParams();

  const authState = useSelector((state) => state.auth);
  const approvalDetailsResponse = useSelector(
    (state) => state.onboarding.approvalDetailsResponse,
  );
  const approvalDetailsStatus = useSelector(
    (state) => state.onboarding.approvalDetailsStatus,
  );
  const jobDetailsDropdownResponse = useSelector(
    (state) => state.onboarding.jobDetailsDropdownResponse,
  );
  const jobDetailsDropdownStatus = useSelector(
    (state) => state.onboarding.jobDetailsDropdownStatus,
  );
  const copyEmployeeDetailsStatus = useSelector(
    (state) => state.onboarding.copyEmployeeDetailsStatus,
  );
  const saveProgressStatus = useSelector(
    (state) => state.onboarding.saveProgressStatus,
  );
  const selectedReviewRequestStatus = useSelector(
    (state) => state.onboarding.selectedReviewRequestStatus,
  );
  const selectedReviewOnboardingId = useSelector(
    (state) => state.onboarding.selectedReviewOnboardingId,
  );

  const mockFallbackData = useMemo(
    () => getReviewRequestById(requestId),
    [requestId],
  );

  const emptyReviewRequestData = useMemo(
    () => ({
      id: requestId || "",
      candidate_name: "",
      designation: "-",
      store_code: "-",
      store_name: "",
      status: ONBOARDING_STATUS.PENDING,
      sections: {
        personal: {},
        address: {},
        education: { highest_qualification: "", records: [] },
        employment: { uan: "", records: [] },
        bank: {},
        insurance: {},
        declarations: {},
        jobDetails: { misc: {} },
      },
      documents: {
        personal: [],
        address: [],
        education: [],
        employment: [],
        bank: [],
        insurance: [],
        declarations: [],
        jobDetails: [],
      },
      updatedFields: [],
      approval_feedback: {},
    }),
    [requestId],
  );

  const apiDetails = useMemo(() => {
    if (approvalDetailsStatus !== "SUCCESS" || !approvalDetailsResponse) {
      return null;
    }
    return normalizeApprovalDetails(approvalDetailsResponse, requestId);
  }, [approvalDetailsResponse, approvalDetailsStatus, requestId]);

  // Prefer mapped API body.data; keep mock only as error fallback
  const initialData = useMemo(
    () =>
      apiDetails ||
      (approvalDetailsStatus === "ERROR" ? mockFallbackData : null) ||
      emptyReviewRequestData,
    [
      apiDetails,
      approvalDetailsStatus,
      mockFallbackData,
      emptyReviewRequestData,
    ],
  );

  // `requestId` comes from the Review Queue row and represents the backend `onboarding_id`.
  const onboardingId = String(
    initialData?.onboarding_id ?? requestId ?? "",
  );

  const jobDetailsOptions = useMemo(() => {
    // Prefer Redux API payload; mocks only before first successful fetch
    if (
      jobDetailsDropdownStatus === "SUCCESS" &&
      jobDetailsDropdownResponse
    ) {
      return normalizeJobDetailsDropdown(jobDetailsDropdownResponse);
    }
    return normalizeJobDetailsDropdown(null);
  }, [jobDetailsDropdownResponse, jobDetailsDropdownStatus]);

  const [navSelected, setNavSelected] = useState("personal");
  const [formData, setFormData] = useState(initialData.sections);
  const [sectionDocuments, setSectionDocuments] = useState(
    () => initialData.documents || {},
  );
  const [previewDoc, setPreviewDoc] = useState(null);
  const [flagModeEnabled, setFlagModeEnabled] = useState(false);
  const [fieldsFilter, setFieldsFilter] = useState(
    UPDATED_FIELDS_FILTER.UPDATED_ONLY,
  );
  // flagKey → { remark, sectionKey, fieldKey }
  const [fieldFlags, setFieldFlags] = useState(() =>
    buildInitialFieldFlags(initialData?.approval_feedback),
  );
  const [flagModal, setFlagModal] = useState(null);
  const [confirmCreateIdOpen, setConfirmCreateIdOpen] = useState(false);
  const [confirmSendBackOpen, setConfirmSendBackOpen] = useState(false);
  const [successCreateIdOpen, setSuccessCreateIdOpen] = useState(false);
  const [createdEmployeeId, setCreatedEmployeeId] = useState("");
  // employee_id returned by save_progress (used for Create ID payload)
  const [draftEmployeeId, setDraftEmployeeId] = useState(null);
  const [focusedFlagKey, setFocusedFlagKey] = useState(null);
  const [pendingFocusFlagKey, setPendingFocusFlagKey] = useState(null);
  const [navIndicator, setNavIndicator] = useState({ left: 0, width: 0 });
  // Primary actions stay disabled until Save as Draft after any change
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(true);
  const [jobDetailsIsError, setJobDetailsIsError] = useState(false);
  const [showJobDetailsValidationError, setShowJobDetailsValidationError] =
    useState(false);
  const [blankExperienceRemarkError, setBlankExperienceRemarkError] =
    useState(false);
  const [hrEditApprovalStatus, setHrEditApprovalStatus] = useState(null);
  // Optimistic-lock version from get_candidate_approval_details / save_progress
  const [requestVersion, setRequestVersion] = useState(null);
  // After save_progress returns body.data.version, do not replace it with the
  // older version still stored on get_candidate_approval_details.
  const saveProgressVersionRef = useRef(false);
  const formNavRef = useRef(null);

  const isResubmittedRequest = useMemo(() => {
    const currentOnboardingId = String(onboardingId || "");
    const storedOnboardingId = String(selectedReviewOnboardingId || "");
    if (
      !currentOnboardingId ||
      !storedOnboardingId ||
      currentOnboardingId !== storedOnboardingId
    ) {
      return false;
    }
    return isResubmittedSummaryStatus(selectedReviewRequestStatus);
  }, [
    onboardingId,
    selectedReviewOnboardingId,
    selectedReviewRequestStatus,
  ]);
  // Terminal / locked statuses → approval actions unavailable.
  // HR-editable statuses (pending, flagged, submitted, etc.) keep Save as Draft,
  // Flag, and Create ID enabled; HR Direct Edit must not lock those buttons.
  // Source of truth: get_candidate_approval_details body.data.approval_status.
  const isReadOnlyRequest = useMemo(() => {
    const statusValue =
      hrEditApprovalStatus ??
      initialData?.raw?.approval_status ??
      initialData?.approval_status ??
      "";
    return isHrApprovalActionLocked(statusValue);
  }, [
    hrEditApprovalStatus,
    initialData?.raw?.approval_status,
    initialData?.approval_status,
  ]);
  const updatedFields = useMemo(
    () =>
      buildUpdatedFieldsFromApprovalFeedback(
        initialData?.approval_feedback || {},
      ),
    [initialData?.approval_feedback],
  );
  const showUpdatedFieldsFilter = isResubmittedRequest;
  const restrictToUpdatedFields =
    showUpdatedFieldsFilter &&
    fieldsFilter === UPDATED_FIELDS_FILTER.UPDATED_ONLY;
  // Read-only statuses: view data + existing feedback only — no flagging
  const effectiveFlagModeEnabled = isReadOnlyRequest ? false : flagModeEnabled;

  useEffect(() => {
    dispatch(
      getCandidateApprovalDetails({
        onboarding_id: onboardingId,
      }),
    );

    return () => {
      dispatch(onboardingActions.resetApprovalDetails());
      dispatch(onboardingActions.resetCopyEmployeeDetails());
      // Keep jobDetailsDropdown in memory for reuse within the SPA session.
    };
  }, [dispatch, requestId]);

  // Fetch job-details dropdowns only when Job Details is opened and not cached
  useEffect(() => {
    if (navSelected !== "jobDetails") return;

    // Reuse successful Redux cache — do not call again
    if (
      jobDetailsDropdownStatus === "SUCCESS" &&
      jobDetailsDropdownResponse
    ) {
      return;
    }

    // Request already in flight
    if (jobDetailsDropdownStatus === "PENDING") return;

    dispatch(
      getJobDetailsDropdown({
        onboarding_id: onboardingId,
      }),
    );
  }, [
    navSelected,
    jobDetailsDropdownStatus,
    jobDetailsDropdownResponse,
    dispatch,
  ]);

  // Sync form fields when approval details arrive / change
  useEffect(() => {
    setFormData(initialData.sections);
    setSectionDocuments(initialData.documents || {});
    setFieldFlags(buildInitialFieldFlags(initialData?.approval_feedback));
  }, [initialData]);

  useEffect(() => {
    if (isReadOnlyRequest) setFlagModeEnabled(false);
  }, [isReadOnlyRequest]);

  // Reset screen session UI only when switching to another request
  useEffect(() => {
    setHrEditApprovalStatus(null);
  }, [
    requestId,
    initialData?.approval_status,
    initialData?.raw?.approval_status,
  ]);

  useEffect(() => {
    setNavSelected("personal");
    setPreviewDoc(null);
    setFlagModeEnabled(false);
    setFieldsFilter(UPDATED_FIELDS_FILTER.UPDATED_ONLY);
    setFlagModal(null);
    setConfirmCreateIdOpen(false);
    setConfirmSendBackOpen(false);
    setSuccessCreateIdOpen(false);
    setCreatedEmployeeId("");
    setDraftEmployeeId(null);
    setFocusedFlagKey(null);
    setPendingFocusFlagKey(null);
    setHasUnsavedChanges(true);
    setJobDetailsIsError(false);
    setShowJobDetailsValidationError(false);
    setBlankExperienceRemarkError(false);
    saveProgressVersionRef.current = false;
    setRequestVersion(null);
  }, [requestId]);

  // Initial version comes from get_candidate_approval_details.
  // A successful save_progress replaces it with body.data.version and
  // this effect must not write the older details version back.
  useEffect(() => {
    if (saveProgressVersionRef.current) return;
    const nextVersion = extractRequestVersion(initialData?.raw ?? initialData);
    if (nextVersion != null) {
      setRequestVersion(nextVersion);
    }
  }, [initialData]);

  useEffect(() => {
    if (!showJobDetailsValidationError) return undefined;
    const timer = setTimeout(() => {
      setShowJobDetailsValidationError(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [showJobDetailsValidationError]);

  useLayoutEffect(() => {
    const updateNavIndicator = () => {
      const nav = formNavRef.current;
      if (!nav) return;
      const activeItem = nav.querySelector(".form-nav-item.active");
      if (!activeItem) return;
      setNavIndicator({
        left: activeItem.offsetLeft,
        width: activeItem.offsetWidth,
      });
    };

    updateNavIndicator();
    window.addEventListener("resize", updateNavIndicator);
    return () => window.removeEventListener("resize", updateNavIndicator);
  }, [navSelected, fieldFlags]);

  useEffect(() => {
    const nav = formNavRef.current;
    if (!nav) return;
    const activeItem = nav.querySelector(".form-nav-item.active");
    if (!activeItem) return;
    activeItem.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [navSelected]);

  useEffect(() => {
    if (!previewDoc) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") setPreviewDoc(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [previewDoc]);

  const queueStatusForHrEdit =
    String(onboardingId || "") &&
    String(onboardingId) === String(selectedReviewOnboardingId || "")
      ? selectedReviewRequestStatus
      : "";
  const markUnsavedAfterHrEdit = useCallback((edits) => {
    // Clearing the pending list after a successful save is not a new edit.
    if (!Array.isArray(edits) || edits.length === 0) return;
    setHasUnsavedChanges(true);
  }, []);
  const handleDocumentReplaced = useCallback(
    ({ sectionKey, docId, storageKey, localFilename }) => {
      const key = String(storageKey || "").trim();
      if (!sectionKey || !docId || !key) return;
      const lower = key.toLowerCase();
      const nextType = lower.endsWith(".pdf")
        ? "pdf"
        : /\.(png|jpe?g|gif|webp)$/.test(lower)
          ? "img"
          : "pdf";
      setSectionDocuments((current) => {
        const list = Array.isArray(current?.[sectionKey])
          ? current[sectionKey]
          : [];
        return {
          ...current,
          [sectionKey]: list.map((doc) =>
            doc?.id === docId
              ? {
                  ...doc,
                  name: localFilename || doc.name,
                  type: nextType,
                  url: key,
                  s3_key: key,
                  file_key: key,
                }
              : doc,
          ),
        };
      });
      setHasUnsavedChanges(true);
    },
    [],
  );
  const hrDirectEdit = useHrDirectEdit({
    approvalStatus: hrEditApprovalStatus ?? initialData?.approval_status,
    queueStatus: queueStatusForHrEdit,
    formData,
    setFormData,
    setFieldFlags,
    rebuildFlags: buildInitialFieldFlags,
    onApprovalStatus: setHrEditApprovalStatus,
    onPendingEditsChange: markUnsavedAfterHrEdit,
    onDocumentReplaced: handleDocumentReplaced,
    onVersionChange: setRequestVersion,
    onboardingId,
    requestVersion,
    requestId,
    editorName: resolveDomainId(authState),
  });
  const hrEditHistoryView = useHrEditHistoryView({
    onboardingId,
    requestId,
  });
  const hrDirectEditContextValue = useMemo(
    () => ({
      ...hrDirectEdit,
      ...hrEditHistoryView,
      hrEditedFields: initialData?.hrEditedFields || {},
    }),
    [hrDirectEdit, hrEditHistoryView, initialData],
  );

  const candidateSubtitle = [
    initialData.designation,
    initialData.store_code,
    initialData.store_name,
  ]
    .filter(Boolean)
    .join(" • ");

  const updateField = (sectionKey, fieldKey, value, recordIndex) => {
    if (isReadOnlyRequest) return;
    setHasUnsavedChanges(true);
    if (sectionKey === "jobDetails") {
      setJobDetailsIsError(false);
      setShowJobDetailsValidationError(false);
    }
    setFormData((prev) => {
      if (sectionKey === "employment" || sectionKey === "education") {
        const sectionData = prev[sectionKey] || {};
        if (recordIndex != null) {
          const records = [...(sectionData.records || [])];
          records[recordIndex] = {
            ...records[recordIndex],
            [fieldKey]: value,
          };
          return {
            ...prev,
            [sectionKey]: {
              ...sectionData,
              records,
            },
          };
        }
        return {
          ...prev,
          [sectionKey]: {
            ...sectionData,
            [fieldKey]: value,
          },
        };
      }

      return {
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          [fieldKey]: value,
        },
      };
    });
  };

  const hasFieldFlags = Object.keys(fieldFlags).length > 0;

  const onSaveAsDraft = async () => {
    if (isReadOnlyRequest) return;
    if (store.getState().onboarding.saveProgressStatus === "PENDING") return;
    if (hasInvalidDateOfJoiningValue(formData?.jobDetails)) {
      setShowJobDetailsValidationError(true);
      setNavSelected("jobDetails");
      return;
    }
    if (hasMissingBlankExperienceRemark(fieldFlags)) {
      setBlankExperienceRemarkError(true);
      setNavSelected("employment");
      dispatch(
        modalActions.setErrorModal({
          message:
            "Please enter a remark for Job Details were left blank, or disable the switch.",
          status: "error",
          state: true,
        }),
      );
      return;
    }
    setBlankExperienceRemarkError(false);
    const payload = buildSaveProgressPayload({
      onboardingId,
      formData,
      fieldFlags,
      domainId: resolveDomainId(authState),
      // Always use candidate Employee ID — never the "Copy from Similar Employee" ID
      employeeId:
        initialData?.employee_id || formData?.jobDetails?.employee_id || null,
      rawDetail: initialData?.raw || null,
      version: requestVersion,
      edits: hrDirectEdit.pendingEdits || [],
    });

    try {
      const response = await dispatch(saveOnboardingProgress(payload)).unwrap();
      hrDirectEdit.clearPendingEdits();
      setHasUnsavedChanges(false);

      const savedVersion = extractVersionFromSaveProgress(response);
      if (Number.isFinite(savedVersion) && savedVersion > 0) {
        saveProgressVersionRef.current = true;
        setRequestVersion(savedVersion);
      }

      const savedEmployeeId = extractEmployeeIdFromSaveProgress(response);
      if (savedEmployeeId != null) {
        setDraftEmployeeId(savedEmployeeId);
        setFormData((prev) => ({
          ...prev,
          jobDetails: {
            ...(prev?.jobDetails || {}),
            employee_id: savedEmployeeId,
          },
        }));
      }
    } catch (error) {
      // Keep pending HR edits and version; error modal is handled in the thunk
    }
  };

  const onPrimaryAction = () => {
    if (isReadOnlyRequest) return;
    if (hasUnsavedChanges) return;
    if (hasFieldFlags) {
      setConfirmSendBackOpen(true);
      return;
    }

    const jobDetailsErrors = validateJobDetailsForCreateId(
      formData?.jobDetails || {},
      commonFields?.user_details,
    );

    if (Object.keys(jobDetailsErrors).length > 0) {
      setJobDetailsIsError(true);
      setShowJobDetailsValidationError(true);
      setNavSelected("jobDetails");
      return;
    }

    setJobDetailsIsError(false);
    setShowJobDetailsValidationError(false);
    setConfirmCreateIdOpen(true);
  };

  const onConfirmSendBack = async () => {
    if (isReadOnlyRequest) return;
    if (store.getState().onboarding.sendBackStatus === "PENDING") return;
    const payload = buildSendBackPayload({
      onboardingId,
      fieldFlags,
      formData,
    });

    try {
      await dispatch(sendBackForCorrection(payload)).unwrap();
      setConfirmSendBackOpen(false);
      navigate("../review-queue");
    } catch (error) {
      // Error modal is handled in the thunk
    }
  };

  const onConfirmCreateId = async () => {
    if (isReadOnlyRequest) return;
    if (store.getState().onboarding.createIdStatus === "PENDING") return;
    const employeeId = String(
      draftEmployeeId ||
        initialData?.employee_id ||
        formData?.jobDetails?.employee_id ||
        "",
    ).trim();
    const payload = buildCreateIdPayload({
      onboardingId,
      employeeId,
    });

    try {
      const response = await dispatch(
        markAsReviewedAndCreateId(payload),
      ).unwrap();
      const createdId = formatCreatedEmployeeId(
        response?.body?.employee_id ||
          response?.employee_id ||
          response?.data?.employee_id ||
          employeeId,
      );

      setConfirmCreateIdOpen(false);
      setCreatedEmployeeId(createdId);
      setSuccessCreateIdOpen(true);
    } catch (error) {
      setConfirmCreateIdOpen(false);
      // Error modal is handled in the thunk
    }
  };

  const onCopyEmployeeDetails = async (employeeId) => {
    if (isReadOnlyRequest) return;
    const id = String(employeeId || "").trim();
    if (!id) return;

    try {
      const response = await dispatch(
        copyEmployeeDetails({ employee_id: id }),
      ).unwrap();
      const copiedDetails = normalizeCopiedEmployeeDetails(response);
      if (!copiedDetails || Object.keys(copiedDetails).length === 0) return;

      setFormData((prev) => ({
        ...prev,
        jobDetails: mergeCopiedJobDetailsIntoForm(
          prev?.jobDetails,
          copiedDetails,
        ),
      }));
      setHasUnsavedChanges(true);
    } catch (error) {
      // Error modal is handled in the thunk
    }
  };

  const onPreviewDoc = async (doc) => {
    if (!doc) return;

    const fileKey = resolveDocumentFileKey(doc);
    // Mock / already-absolute URLs: keep existing viewer behavior
    if (!fileKey) {
      if (doc.url) setPreviewDoc(doc);
      return;
    }

    try {
      const response = await dispatch(
        generatePresignUrl({ file_key: fileKey }),
      ).unwrap();
      const presignedUrl = extractPresignedUrl(response);

      if (!presignedUrl) {
        dispatch(
          modalActions.setErrorModal({
            message: "Error : Unable to generate document preview URL",
            status: "error",
            state: true,
          }),
        );
        return;
      }

      setPreviewDoc({
        ...doc,
        url: presignedUrl,
      });
    } catch (error) {
      // Loader + error modal are handled in the thunk
    }
  };

  const onBackToQueueAfterCreateId = () => {
    setSuccessCreateIdOpen(false);
    setCreatedEmployeeId("");
    navigate("../review-queue");
  };

  const openFlagModal = (sectionKey, field, event, options = {}) => {
    if (isReadOnlyRequest || !flagModeEnabled || isFlagBlockedField(field))
      return;
    const { recordIndex = null, recordLabel = "", isDocument = false } =
      options;
    if (
      restrictToUpdatedFields &&
      !isUpdatedReviewField(
        updatedFields,
        sectionKey,
        field.key,
        isDocument ? null : recordIndex,
      )
    ) {
      return;
    }
    const flagKey = getFlagKey(sectionKey, field.key, recordIndex);
    const existing = fieldFlags[flagKey];

    const areaEl = event?.currentTarget?.closest?.(".rr-field-input-area");
    const inputEl =
      areaEl?.querySelector?.(".emp-form-input") ||
      areaEl?.querySelector?.(".rr-document-card") ||
      event?.currentTarget ||
      areaEl;

    const readAnchor = (el) => {
      const rect = el?.getBoundingClientRect?.();
      if (!rect) return null;
      return {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        bottom: rect.bottom,
        height: rect.height,
      };
    };

    const buildModalState = (anchor) => {
      const viewportHeight = window.innerHeight;
      const top = anchor?.top ?? 0;
      const bottom = anchor?.bottom ?? top;
      const inTopZone = top < viewportHeight * 0.32;
      const inBottomZone = bottom > viewportHeight * 0.68;

      let preferredPlacement = "below";
      if (inTopZone) preferredPlacement = "below";
      else if (inBottomZone) preferredPlacement = "above";
      else {
        const spaceAbove = top - 12;
        const spaceBelow = viewportHeight - bottom - 12;
        preferredPlacement = spaceBelow >= spaceAbove ? "below" : "above";
      }

      return {
        flagKey,
        sectionKey,
        fieldKey: field.key,
        fieldLabel: recordLabel
          ? `${recordLabel} · ${field.label}`
          : field.label,
        recordIndex,
        mode: existing ? "edit" : "add",
        remark: existing?.remark || "",
        anchor,
        preferredPlacement,
      };
    };

    let anchor = readAnchor(inputEl);
    const viewportHeight = window.innerHeight;
    const inMiddleZone =
      anchor &&
      anchor.top >= viewportHeight * 0.32 &&
      anchor.bottom <= viewportHeight * 0.68;

    // Middle of the screen: scroll field into a clearer spot, then open
    if (inMiddleZone && inputEl) {
      const spaceAbove = anchor.top - 12;
      const spaceBelow = viewportHeight - anchor.bottom - 12;
      const preferBelow = spaceBelow >= spaceAbove;
      const scrollParent =
        inputEl.closest?.(".form-contents") ||
        inputEl.closest?.(".rr-form-panel");

      if (scrollParent) {
        const parentRect = scrollParent.getBoundingClientRect();
        const inputRect = inputEl.getBoundingClientRect();
        const offsetInParent =
          inputRect.top - parentRect.top + scrollParent.scrollTop;
        const targetScroll = preferBelow
          ? Math.max(0, offsetInParent - 24)
          : Math.max(
              0,
              offsetInParent -
                (scrollParent.clientHeight - inputRect.height - 24),
            );
        scrollParent.scrollTo({ top: targetScroll, behavior: "smooth" });
      } else if (inputEl.scrollIntoView) {
        inputEl.scrollIntoView({
          behavior: "smooth",
          block: preferBelow ? "start" : "end",
          inline: "nearest",
        });
      }

      window.setTimeout(() => {
        const refreshed = readAnchor(inputEl);
        setFlagModal(buildModalState(refreshed || anchor));
      }, 300);
      return;
    }

    setFlagModal(buildModalState(anchor));
  };

  const closeFlagModal = () => setFlagModal(null);

  const saveFieldFlag = (remark) => {
    if (isReadOnlyRequest || !flagModal) return;
    const field = findReviewField(flagModal.sectionKey, flagModal.fieldKey);
    if (field && isFlagBlockedField(field)) {
      setFlagModal(null);
      return;
    }
    const { flagKey, sectionKey, fieldKey, recordIndex, fieldLabel } =
      flagModal;
    const now = new Date().toISOString();
    setHasUnsavedChanges(true);
    setFieldFlags((prev) => {
      const existing = prev[flagKey];
      return {
        ...prev,
        [flagKey]: {
          remark,
          sectionKey,
          fieldKey,
          recordIndex: recordIndex ?? null,
          fieldLabel: fieldLabel || field?.label || fieldKey,
          // Keep original add time; refresh updatedAt when editing
          flaggedAt: existing?.flaggedAt || now,
          updatedAt: now,
        },
      };
    });
    setFlagModal(null);
  };

  const removeFieldFlag = (flagKeyToRemove) => {
    if (isReadOnlyRequest) return;
    const flagKey =
      typeof flagKeyToRemove === "string" ? flagKeyToRemove : flagModal?.flagKey;
    if (!flagKey) return;
    setHasUnsavedChanges(true);
    setFieldFlags((prev) => {
      const next = { ...prev };
      delete next[flagKey];
      return next;
    });
    if (flagModal?.flagKey === flagKey) {
      setFlagModal(null);
    }
  };

  const onToggleBlankExperienceFlags = (enabled, remark = "") => {
    if (isReadOnlyRequest) return;
    if (enabled && !flagModeEnabled) return;
    const now = new Date().toISOString();
    const sharedRemark = String(remark || "");
    setHasUnsavedChanges(true);
    setFieldFlags((prev) => {
      const next = { ...prev };
      BLANK_EMPLOYMENT_EXPERIENCE_FLAGS.forEach((item) => {
        const flagKey = getFlagKey(
          "employment",
          item.fieldKey,
          item.recordIndex,
        );
        if (enabled) {
          const existing = next[flagKey];
          next[flagKey] = {
            remark: sharedRemark,
            sectionKey: "employment",
            fieldKey: item.fieldKey,
            recordIndex: item.recordIndex,
            fieldLabel: item.fieldLabel,
            flaggedAt: existing?.flaggedAt || now,
            updatedAt: now,
          };
        } else {
          delete next[flagKey];
        }
      });
      return next;
    });
  };

  const onUpdateBlankExperienceRemark = (remark = "") => {
    if (isReadOnlyRequest || !flagModeEnabled) return;
    const sharedRemark = String(remark || "");
    const now = new Date().toISOString();
    setHasUnsavedChanges(true);
    setFieldFlags((prev) => {
      const next = { ...prev };
      let changed = false;
      BLANK_EMPLOYMENT_EXPERIENCE_FLAGS.forEach((item) => {
        const flagKey = getFlagKey(
          "employment",
          item.fieldKey,
          item.recordIndex,
        );
        if (!next[flagKey]) return;
        changed = true;
        next[flagKey] = {
          ...next[flagKey],
          remark: sharedRemark,
          updatedAt: now,
        };
      });
      return changed ? next : prev;
    });
  };

  const activeSection = REVIEW_REQUEST_SECTIONS.find(
    (section) => section.key === navSelected,
  );
  const activeFields = REVIEW_SECTION_FIELDS[navSelected] || [];
  const activeDocuments = sectionDocuments?.[navSelected] || [];
  const hrEditedCountSources = useMemo(
    () => ({
      hrEditedFields: initialData?.hrEditedFields,
      history: hrDirectEdit.history,
    }),
    [hrDirectEdit.history, initialData?.hrEditedFields],
  );
  const allEditedFieldCount = useMemo(
    () => countHrEditedFields(hrEditedCountSources),
    [hrEditedCountSources],
  );
  const sectionEditedCount = useMemo(
    () =>
      countHrEditedFields({
        ...hrEditedCountSources,
        sectionKey: navSelected,
      }),
    [hrEditedCountSources, navSelected],
  );

  const flaggedSummaryItems = useMemo(() => {
    return Object.entries(fieldFlags)
      .map(([key, value]) => {
        const section = REVIEW_REQUEST_SECTIONS.find(
          (item) => item.key === value.sectionKey,
        );
        const field = findReviewField(value.sectionKey, value.fieldKey);
        if (field && isFlagBlockedField(field)) return null;

        let fieldLabel = value.fieldLabel || field?.label || value.fieldKey;
        if (
          value.sectionKey === "employment" &&
          value.recordIndex != null &&
          !value.fieldLabel
        ) {
          const company =
            formData?.employment?.records?.[value.recordIndex]?.company || "";
          fieldLabel = company
            ? `${company} · ${field?.label || value.fieldKey}`
            : field?.label || value.fieldKey;
        }
        if (
          value.sectionKey === "education" &&
          value.recordIndex != null &&
          !value.fieldLabel
        ) {
          const qualification =
            formData?.education?.records?.[value.recordIndex]?.qualification ||
            "";
          const qualificationLabel =
            getEducationQualificationLabel(qualification);
          fieldLabel = qualificationLabel
            ? `${qualificationLabel} · ${field?.label || value.fieldKey}`
            : field?.label || value.fieldKey;
        }

        return {
          key,
          sectionKey: value.sectionKey,
          fieldKey: value.fieldKey,
          recordIndex: value.recordIndex ?? null,
          sectionLabel: section?.label || value.sectionKey,
          fieldLabel,
          remark: value.remark || "",
          flaggedAt: value.flaggedAt || value.updatedAt || "",
          timeLabel: formatFlagTime(value.flaggedAt || value.updatedAt),
        };
      })
      .filter(Boolean);
  }, [fieldFlags, formData?.employment, formData?.education]);

  const sectionFlagCounts = useMemo(() => {
    const counts = {};
    Object.values(fieldFlags).forEach((flag) => {
      const sectionKey = flag?.sectionKey;
      if (!sectionKey) return;
      const field = findReviewField(sectionKey, flag.fieldKey);
      if (field && isFlagBlockedField(field)) return;
      counts[sectionKey] = (counts[sectionKey] || 0) + 1;
    });
    return counts;
  }, [fieldFlags]);

  const scrollToFlaggedField = (flagKey) => {
    if (!flagKey) return false;
    const escapedKey =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(flagKey)
        : String(flagKey).replace(/"/g, '\\"');
    const fieldEl = document.querySelector(
      `[data-rr-flag-key="${escapedKey}"]`,
    );
    if (!fieldEl) return false;

    const scrollParent =
      fieldEl.closest?.(".form-contents") ||
      fieldEl.closest?.(".rr-form-panel");

    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      const fieldRect = fieldEl.getBoundingClientRect();
      const nextTop =
        scrollParent.scrollTop + (fieldRect.top - parentRect.top) - 24;
      scrollParent.scrollTo({
        top: Math.max(0, nextTop),
        behavior: "smooth",
      });
    } else {
      fieldEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    setFocusedFlagKey(flagKey);
    return true;
  };

  const goToFlaggedField = (item) => {
    if (!item?.sectionKey || !item?.key) return;

    if (navSelected !== item.sectionKey) {
      setPendingFocusFlagKey(item.key);
      setNavSelected(item.sectionKey);
      return;
    }

    scrollToFlaggedField(item.key);
  };

  useLayoutEffect(() => {
    if (!pendingFocusFlagKey) return undefined;
    const didScroll = scrollToFlaggedField(pendingFocusFlagKey);
    if (didScroll) {
      setPendingFocusFlagKey(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      scrollToFlaggedField(pendingFocusFlagKey);
      setPendingFocusFlagKey(null);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [navSelected, pendingFocusFlagKey]);

  useEffect(() => {
    if (!focusedFlagKey) return undefined;
    const timer = window.setTimeout(() => setFocusedFlagKey(null), 1600);
    return () => window.clearTimeout(timer);
  }, [focusedFlagKey]);

  return (
    <HrDirectEditContext.Provider value={hrDirectEditContextValue}>
    <div className={`flex flex-col rr-home${isReadOnlyRequest ? " rr-home--readonly" : ""}`}>
      {showJobDetailsValidationError ? (
        <div className="flex vl-err">
          <img src={ValidIcon} alt="err-ic" />
          <div className="flex flex-col">
            <div className="vl-err-main">
              Uh oh! It looks like some fields are incomplete
            </div>
            <div className="vl-err-subtext">
              Please navigate to the below sections to complete all details
            </div>
            <div className="vl-err-value capitalize">job details</div>
          </div>
          <div className="vl-close">
            <img
              className="vl-close-ico"
              src={CloseIcon}
              alt="close-ico"
              onClick={() => setShowJobDetailsValidationError(false)}
            />
          </div>
        </div>
      ) : null}
      <div className="flex justify-between items-center rr-header">
        <div className="flex items-center rr-header-left">
          <img
            className="arrow-icon"
            src={ArrowbackIcon}
            alt="arrow-back"
            onClick={() => navigate("../review-queue")}
          />
          <InitialsCircle fullName={initialData.candidate_name} size={40} />
          <div className="rr-title-wrap">
            <div className="rr-candidate-name">
              {initialData.candidate_name}
            </div>
            <div className="rr-candidate-role">{candidateSubtitle}</div>
          </div>
        </div>

        <div className="flex items-center rr-header-actions">
          {showUpdatedFieldsFilter ? (
            <UpdatedFieldsFilter
              value={fieldsFilter}
              onChange={setFieldsFilter}
            />
          ) : null}
          {allEditedFieldCount > 0 ? (
            <button
              type="button"
              className="rr-hr-edit-count"
              onClick={() => hrDirectEdit.setHistoryOpen((open) => !open)}
            >
              {hrEditedFieldsCountLabel(allEditedFieldCount)}
            </button>
          ) : null}
          <button
            type="button"
            className={`rr-flag-mode-switch ${
              effectiveFlagModeEnabled ? "is-on" : ""
            }${isReadOnlyRequest ? " is-disabled" : ""}`}
            role="switch"
            aria-checked={effectiveFlagModeEnabled}
            aria-disabled={isReadOnlyRequest}
            disabled={isReadOnlyRequest}
            aria-label={
              effectiveFlagModeEnabled ? "Disable Flag Mode" : "Enable Flag Mode"
            }
            title={
              isReadOnlyRequest
                ? "Flag Mode unavailable while request is read-only"
                : effectiveFlagModeEnabled
                  ? "Flag Mode on"
                  : "Flag Mode off"
            }
            onClick={() => {
              if (isReadOnlyRequest) return;
              setFlagModeEnabled((prev) => !prev);
            }}
          >
            <span className="rr-flag-mode-switch__thumb" aria-hidden="true" />
            <span
              className="material-symbols-rounded rr-flag-mode-switch__flag"
              aria-hidden="true"
            >
              flag
            </span>
          </button>
          <div className="rr-flag-mode-tip">
            <button
              type="button"
              className="rr-flag-mode-tip__trigger"
              aria-label="Flag Mode tip"
              aria-describedby="rr-flag-mode-tip-popover"
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                help
              </span>
            </button>
            <div
              id="rr-flag-mode-tip-popover"
              className="rr-flag-mode-tip__popover"
              role="tooltip"
            >
              <span className="rr-flag-mode-tip__badge">tip</span>
              <p className="rr-flag-mode-tip__text">
                Use the{" "}
                <span className="rr-flag-mode-tip__chip">
                  <span
                    className="material-symbols-rounded rr-flag-mode-tip__chip-icon"
                    aria-hidden="true"
                  >
                    flag
                  </span>
                  Flag
                </span>{" "}
                tool to mark mistakes and wrong information
              </p>
            </div>
          </div>
          <button
            type="button"
            className={`btn p-btn p-btn-blk${
              saveProgressStatus === "PENDING" ? " is-saving" : ""
            }`}
            onClick={onSaveAsDraft}
            disabled={isReadOnlyRequest || saveProgressStatus === "PENDING"}
            title={
              isReadOnlyRequest
                ? "Save as Draft unavailable while request is read-only"
                : saveProgressStatus === "PENDING"
                  ? "Saving draft"
                  : undefined
            }
          >
            <img className="p-btn-icon" src={DraftIcon} alt="" />
            <div className="p-btn-text">Save as Draft</div>
          </button>
          {isReadOnlyRequest ? (
            <button
              type="button"
              className="btn p-btn p-btn-og"
              disabled
              title="Actions unavailable while request is read-only"
            >
              <img className="p-btn-icon" src={SubmitIcon} alt="" />
              <div className="p-btn-text">Create ID</div>
            </button>
          ) : hasUnsavedChanges ? (
            <div className="rr-primary-action-tip">
              <button
                type="button"
                className="btn p-btn p-btn-og"
                onClick={onPrimaryAction}
                disabled
                aria-describedby="rr-primary-action-tip-popover"
              >
                {hasFieldFlags ? (
                  <span
                    className="material-symbols-rounded"
                    style={{ fontSize: "16px", color: "white" }}
                    aria-hidden="true"
                  >
                    forward
                  </span>
                ) : (
                  <img className="p-btn-icon" src={SubmitIcon} alt="" />
                )}
                <div className="p-btn-text">
                  {hasFieldFlags ? "Flag to candidate" : "Create ID"}
                </div>
              </button>
              <div
                id="rr-primary-action-tip-popover"
                className="rr-flag-mode-tip__popover rr-primary-action-tip__popover"
                role="tooltip"
              >
                <span className="rr-flag-mode-tip__badge">tip</span>
                <p className="rr-flag-mode-tip__text">
                  This button needs a save to wake up.
                </p>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn p-btn p-btn-og"
              onClick={onPrimaryAction}
            >
              {hasFieldFlags ? (
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: "16px", color: "white" }}
                  aria-hidden="true"
                >
                  forward
                </span>
              ) : (
                <img className="p-btn-icon" src={SubmitIcon} alt="" />
              )}
              <div className="p-btn-text">
                {hasFieldFlags ? "Flag to candidate" : "Create ID"}
              </div>
            </button>
          )}
        </div>
      </div>

      <div className="flex form-nav" ref={formNavRef}>
        <span
          className="rr-nav-indicator-bg"
          style={{
            transform: `translateX(${navIndicator.left}px)`,
            width: navIndicator.width,
          }}
          aria-hidden="true"
        />
        <span
          className="rr-nav-indicator-line"
          style={{
            transform: `translateX(${navIndicator.left}px)`,
            width: navIndicator.width,
          }}
          aria-hidden="true"
        />
        {REVIEW_REQUEST_SECTIONS.map((section) => {
          const flagCount = sectionFlagCounts[section.key] || 0;
          return (
            <div
              key={section.key}
              className={`flex items-center form-nav-item ${
                navSelected === section.key ? "active" : ""
              }`}
              onClick={() => setNavSelected(section.key)}
            >
              <img
                src={section.icon}
                alt={`${section.label}-icon`}
                className="nav-icon"
              />
              <div className="nav-text">{section.label}</div>
              {flagCount > 0 ? (
                <span
                  className="rr-section-flag-badge"
                  aria-label={`${flagCount} flagged field${
                    flagCount === 1 ? "" : "s"
                  }`}
                >
                  {flagCount}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="rr-body">
        <div className="rr-form-panel">
          <div className="form-contents flex flex-col">
            <div className="rr-section" id={navSelected}>
              <div className="flex items-center resume-head">
                {activeSection && (
                  <img
                    className="nav-icon"
                    src={activeSection.icon}
                    alt={`${activeSection.label}-icon`}
                  />
                )}
                <div className="form-subhead">
                  {activeSection?.label?.toLowerCase()}
                </div>
                {sectionEditedCount > 0 ? (
                  <button
                    type="button"
                    className="rr-hr-edit-count"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      hrDirectEdit.setHistoryOpen((open) => !open);
                    }}
                  >
                    {hrEditedFieldsCountLabel(sectionEditedCount)}
                  </button>
                ) : null}
              </div>

              {navSelected === "personal" ? (
                <PersonalSection
                  fields={activeFields}
                  formData={formData}
                  fieldFlags={fieldFlags}
                  flagModeEnabled={effectiveFlagModeEnabled}
                  restrictToUpdatedFields={restrictToUpdatedFields}
                  updatedFields={updatedFields}
                  flagModal={flagModal}
                  focusedFlagKey={focusedFlagKey}
                  onOpenFlagModal={openFlagModal}
                  onUpdateField={updateField}
                  documents={activeDocuments}
                  getFileIcon={getFileIcon}
                  onPreviewDoc={onPreviewDoc}
                />
              ) : null}
              {navSelected === "address" ? (
                <AddressSection
                  fields={activeFields}
                  formData={formData}
                  fieldFlags={fieldFlags}
                  flagModeEnabled={effectiveFlagModeEnabled}
                  restrictToUpdatedFields={restrictToUpdatedFields}
                  updatedFields={updatedFields}
                  flagModal={flagModal}
                  focusedFlagKey={focusedFlagKey}
                  onOpenFlagModal={openFlagModal}
                  onUpdateField={updateField}
                  documents={activeDocuments}
                  getFileIcon={getFileIcon}
                  onPreviewDoc={onPreviewDoc}
                />
              ) : null}
              {navSelected === "education" ? (
                <EducationSection
                  formData={formData}
                  fieldFlags={fieldFlags}
                  flagModeEnabled={effectiveFlagModeEnabled}
                  restrictToUpdatedFields={restrictToUpdatedFields}
                  updatedFields={updatedFields}
                  flagModal={flagModal}
                  focusedFlagKey={focusedFlagKey}
                  onOpenFlagModal={openFlagModal}
                  onUpdateField={updateField}
                  documents={activeDocuments}
                  getFileIcon={getFileIcon}
                  onPreviewDoc={onPreviewDoc}
                />
              ) : null}
              {navSelected === "employment" ? (
                <EmploymentSection
                  formData={formData}
                  fieldFlags={fieldFlags}
                  flagModeEnabled={effectiveFlagModeEnabled}
                  restrictToUpdatedFields={restrictToUpdatedFields}
                  updatedFields={updatedFields}
                  flagModal={flagModal}
                  focusedFlagKey={focusedFlagKey}
                  onOpenFlagModal={openFlagModal}
                  onUpdateField={updateField}
                  documents={activeDocuments}
                  getFileIcon={getFileIcon}
                  onPreviewDoc={onPreviewDoc}
                  candidateName={initialData.candidate_name}
                  readOnly={isReadOnlyRequest}
                  onToggleBlankExperienceFlags={onToggleBlankExperienceFlags}
                  onUpdateBlankExperienceRemark={onUpdateBlankExperienceRemark}
                  blankExperienceRemarkError={blankExperienceRemarkError}
                  onBlankExperienceRemarkErrorChange={
                    setBlankExperienceRemarkError
                  }
                />
              ) : null}
              {navSelected === "bank" ? (
                <BankSection
                  fields={activeFields}
                  formData={formData}
                  fieldFlags={fieldFlags}
                  flagModeEnabled={effectiveFlagModeEnabled}
                  restrictToUpdatedFields={restrictToUpdatedFields}
                  updatedFields={updatedFields}
                  flagModal={flagModal}
                  focusedFlagKey={focusedFlagKey}
                  onOpenFlagModal={openFlagModal}
                  onUpdateField={updateField}
                  documents={activeDocuments}
                  getFileIcon={getFileIcon}
                  onPreviewDoc={onPreviewDoc}
                />
              ) : null}
              {navSelected === "insurance" ? (
                <InsuranceSection
                  fields={activeFields}
                  formData={formData}
                  fieldFlags={fieldFlags}
                  flagModeEnabled={effectiveFlagModeEnabled}
                  restrictToUpdatedFields={restrictToUpdatedFields}
                  updatedFields={updatedFields}
                  flagModal={flagModal}
                  focusedFlagKey={focusedFlagKey}
                  onOpenFlagModal={openFlagModal}
                  onUpdateField={updateField}
                  documents={activeDocuments}
                  getFileIcon={getFileIcon}
                  onPreviewDoc={onPreviewDoc}
                />
              ) : null}
              {navSelected === "declarations" ? (
                <DeclarationSection
                  fields={activeFields}
                  formData={formData}
                  fieldFlags={fieldFlags}
                  flagModeEnabled={effectiveFlagModeEnabled}
                  restrictToUpdatedFields={restrictToUpdatedFields}
                  updatedFields={updatedFields}
                  flagModal={flagModal}
                  focusedFlagKey={focusedFlagKey}
                  onOpenFlagModal={openFlagModal}
                  onUpdateField={updateField}
                  documents={activeDocuments}
                  getFileIcon={getFileIcon}
                  onPreviewDoc={onPreviewDoc}
                />
              ) : null}
              {navSelected === "jobDetails" ? (
                <JobDetailsSection
                  data={formData?.jobDetails || {}}
                  options={jobDetailsOptions}
                  isError={jobDetailsIsError}
                  readOnly={isReadOnlyRequest}
                  isCopyLoading={copyEmployeeDetailsStatus === "PENDING"}
                  onCopyEmployeeDetails={onCopyEmployeeDetails}
                  onChange={(fieldKey, value) =>
                    updateField("jobDetails", fieldKey, value)
                  }
                />
              ) : null}
            </div>
          </div>
        </div>

        <div className="rr-summary-panel">
          <div className="rr-summary-title">Review summary</div>
          {flaggedSummaryItems.length === 0 ? (
            <div className="rr-summary-empty">
              {hrDirectEdit.enabled
                ? "No corrections requested yet. Flag anything the candidate needs to fix, or use the pencil to correct small mistakes yourself."
                : "No corrections requested yet. Flag any field or document that needs a fix."}
            </div>
          ) : (
            <div className="rr-summary-list">
              {flaggedSummaryItems.map((item) => (
                <div
                  key={item.key}
                  className="rr-summary-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => goToFlaggedField(item)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      goToFlaggedField(item);
                    }
                  }}
                >
                  <div className="rr-summary-item-header">
                    <div className="rr-summary-item-field">{item.fieldLabel}</div>
                    {!isReadOnlyRequest ? (
                      <button
                        type="button"
                        className="rr-summary-item-remove"
                        title={`Remove flag for ${item.fieldLabel}`}
                        aria-label={`Remove flag for ${item.fieldLabel}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          removeFieldFlag(item.key);
                        }}
                      >
                        <img src={DeleteIcon} alt="" />
                      </button>
                    ) : null}
                  </div>
                  <div className="rr-summary-item-remark">{item.remark}</div>
                  <div className="rr-summary-item-meta">
                    <span className="rr-summary-item-section">
                      {item.sectionLabel}
                    </span>
                    {item.timeLabel && (
                      <span className="rr-summary-item-time">
                        {item.timeLabel}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <HrEditHistoryPanel entries={hrDirectEdit.history} />
        </div>
      </div>

      <FlagModal
        open={!!flagModal}
        mode={flagModal?.mode || "add"}
        fieldLabel={flagModal?.fieldLabel || ""}
        initialComment={flagModal?.remark || ""}
        anchor={flagModal?.anchor || null}
        preferredPlacement={flagModal?.preferredPlacement || null}
        onClose={closeFlagModal}
        onSave={saveFieldFlag}
        onRemove={() => removeFieldFlag()}
      />

      <ConfirmCreateIdModal
        open={confirmCreateIdOpen}
        onClose={() => setConfirmCreateIdOpen(false)}
        onProceed={onConfirmCreateId}
      />

      <ConfirmSendBackModal
        open={confirmSendBackOpen}
        candidateName={initialData.candidate_name}
        onClose={() => setConfirmSendBackOpen(false)}
        onConfirm={onConfirmSendBack}
      />

      <SuccessCreateIdModal
        open={successCreateIdOpen}
        candidateName={initialData.candidate_name}
        employeeId={createdEmployeeId}
        onBackToQueue={onBackToQueueAfterCreateId}
      />

      {previewDoc && (
        <div className="rr-doc-viewer" role="dialog" aria-modal="true">
          <div className="rr-doc-viewer-header">
            <div className="rr-doc-viewer-title">{previewDoc.name}</div>
            <button
              type="button"
              className="rr-doc-viewer-close"
              aria-label="Close document preview"
              onClick={() => setPreviewDoc(null)}
            >
              <img src={CloseIcon} alt="" />
            </button>
          </div>
          <div className="rr-doc-viewer-body">
            {previewDoc.type === "pdf" && previewDoc.url ? (
              <Worker workerUrl={PDF_WORKER_URL}>
                <Viewer fileUrl={resolveDocUrl(previewDoc.url)} />
              </Worker>
            ) : previewDoc.type === "img" && previewDoc.url ? (
              <img
                className="rr-doc-viewer-image"
                src={resolveDocUrl(previewDoc.url)}
                alt={previewDoc.name}
              />
            ) : (
              <div className="rr-doc-viewer-fallback">
                <img
                  className="rr-doc-viewer-fallback-icon"
                  src={getFileIcon(previewDoc.type)}
                  alt=""
                />
                <div>Preview is not available for this file.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </HrDirectEditContext.Provider>
  );
};

export default ReviewRequest;
